
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import clsx from 'clsx'
import {MDXRemote, MDXRemoteSerializeResult} from 'next-mdx-remote'
import {serialize} from 'next-mdx-remote/serialize'
import useTranslation from 'next-translate/useTranslation'
import {useRouter} from 'next/router'
import {
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react'
import {useForm} from 'react-hook-form'
import {useInView} from 'react-intersection-observer/useInView'
import ActivityIndicator from '../../components/atoms/activity-indicator'
import Button from '../../components/atoms/button'
import Container from '../../components/atoms/container'
import Heading from '../../components/atoms/heading'
import MailIcon from '../../components/atoms/icons/mail-icon'
import PhoneIcon from '../../components/atoms/icons/phone-icon'
import InputField from '../../components/atoms/input-field'
import List from '../../components/atoms/list'
import ListItem from '../../components/atoms/list-item'
import Paragraph from '../../components/atoms/paragraph'
import Avatar from '../../components/molecules/avatar'
import ContactForm from '../../components/molecules/contact-form'
import TeamMemberCard from '../../components/molecules/team-member-card'
import FileInput from '../../components/organisms/file-input'
import NextStepsSection from '../../components/templates/next-steps-section'
import {
  FooterDataStateProps,
  FooterProvider,
} from '../../contexts/footer-context'
import useAlert from '../../hooks/use-alert'
import PageLayout from '../../layouts/page-layout'
import fetchContactUsData, {
  ContactUsDataProps,
} from '../../lib/fetch-contact-us-data'
import fetchGeneralSettings from '../../lib/fetch-general-settings'
import fetchSocialLinks from '../../lib/fetch-social-links'
import mapRawTeamMemberToTeamMember from '../../lib/map-raw-team-member-to-team-member'
import bishopSymbol from '../../public/assets/bishop-logo-symbol.png'

export interface CareerContactUsFormProps {
  name: string
  email: string
  phone: string
  position: string
  interest: string
  attachments?: FileList
}

export interface CareerContactUsProps
  extends FooterDataStateProps,
    ContactUsDataProps<MDXRemoteSerializeResult> {
  mailApiUrl: string
}

function CustomParagraph({children}: PropsWithChildren<unknown>) {
  return <Paragraph className="text-center">{children}</Paragraph>
}

export default function CareerContactUs({
  coverTitle,
  coverDescription,
  metaDescription,
  previewImage,
  responsiblePeople,
  nextSteps,
  mailApiUrl,
  social,
  general,
}: CareerContactUsProps) {
  const [files, setFiles] = useState<Array<File>>([])
  const {setAlert, clearAlert} = useAlert()
  const {t} = useTranslation('contact-us')
  const {query, replace} = useRouter()
  const {ref: formRef, inView: formInView} = useInView({
    triggerOnce: true,
  })
  const {ref: nextStepsRef, inView: nextStepsInView} = useInView({
    triggerOnce: true,
  })

  const {
    register,
    handleSubmit: onSubmit,
    formState: {errors, isSubmitting},
    setError,
    setValue,
    reset,
  } = useForm<CareerContactUsFormProps>({
    defaultValues: {
      position:
        query.job && typeof query.job === 'string' ? query.job : undefined,
    },
  })

  useEffect(() => {
    if (query.job && typeof query.job === 'string') {
      reset({position: query.job})
    } else if (!query.job) {
      reset({position: undefined})
    }
  }, [query, reset])

  function handleOpenUrl(url: string) {
    return (
      event: MouseEvent<HTMLLIElement> | KeyboardEvent<HTMLLIElement>,
    ) => {
      if (
        event.type === 'keydown' &&
        (event as KeyboardEvent).key !== 'Enter' &&
        (event as KeyboardEvent).key !== ' '
      ) {
        return
      }

      event.preventDefault()

      window.open(url.replace(/ /gi, ''), '_self')
    }
  }

  function handleFileRemove(fileList: FileList | Array<File>) {
    if (fileList instanceof FileList) {
      setValue('attachments', fileList)
    }
  }

  async function handleSubmit(data: CareerContactUsFormProps) {
    clearAlert()

    if (data.attachments && data.attachments.length) {
      if (data.attachments.length > 3) {
        setError('attachments', {
          message: t('common:validation.max-files', {count: 3}),
        })

        return
      }

      let filesWithSizeError: Array<string> = []

      for (const file of data.attachments) {
        if (file.size / 1024 / 1024 > 2) {
          filesWithSizeError = filesWithSizeError.concat(file.name)
        }
      }

      if (filesWithSizeError.length > 0) {
        setError('attachments', {
          message: t('common:validation.size-limit', {
            count: filesWithSizeError.length,
            limit: '2 MB',
            files: filesWithSizeError.join(', '),
          }),
        })

        return
      }
    }

    const formData = new FormData()

    formData.append('name', data.name)
    formData.append('email', data.email)
    formData.append('phone', data.phone)
    formData.append('position', data.position)
    formData.append('interest', data.interest)

    if (data.attachments) {
      for (let i = 0; i < data.attachments.length; i++) {
        const currentFile = data.attachments.item(i)

        if (currentFile) {
          formData.append('attachments', currentFile, currentFile.name)
        }
      }
    }

    try {
      const response = await fetch(`${mailApiUrl}/apply`, {
        method: 'POST',
        body: formData,
      })

      const responseData = await response.json()

      if (response.ok) {
        reset(undefined, {keepDefaultValues: false})
        replace('/contact-us/apply', undefined, {shallow: true})
        setFiles([])

        setAlert(responseData.message, 'success')
      } else {
        setAlert(responseData.message, 'error')
      }
    } catch (error) {
      setAlert(t('common:error.message-unknown'), 'error')
    }
  }

  return (
    <FooterProvider value={{social, general}}>
      <PageLayout
        disableDefaultPadding
        className={clsx(
          'grid grid-flow-row gap-8 pt-28 md:pt-32 content-center',
          (!nextSteps || nextSteps.length === 0) && 'pb-40',
        )}
        title={t('common:navigation.contact-us')}
        description={metaDescription}
        openGraph={{
          images: [{url: previewImage || bishopSymbol.src}],
          url: "contact-us/apply"
        }}
        prefixImageUrl={!previewImage}
      >
        <Container
          component="section"
          className="grid justify-center motion-safe:animate-fade-in-slide-up"
        >
          <Heading variant="h1" className="text-center" disableMaxWidth>
            {coverTitle}
          </Heading>

          <MDXRemote
            {...coverDescription}
            components={{
              p: CustomParagraph,
            }}
          />
        </Container>

        <Container
          component="section"
          className={clsx(
            formInView
              ? 'motion-safe:animate-fade-in-slide-up-delay-sm'
              : 'opacity-0',
          )}
          ref={formRef}
        >
          <ContactForm className="grid grid-flow-row lg:grid-flow-col lg:grid-cols-3 gap-6">
            <div className="grid grid-flow-row bg-highlighted rounded-lg p-4 items-center content-center gap-8 col-span-12 lg:col-auto">
              <div className="grid gap-4 justify-center sm:justify-items-start sm:gap-8 sm:grid-flow-col">
                {responsiblePeople.map(({name, role, image}) => (
                  <TeamMemberCard
                    name={name}
                    role={role}
                    key={`${name}-${role}`}
                    disableDefaultAnimation
                  >
                    <Avatar
                      src={image}
                      alt={`${name} / ${role}`}
                      size={75}
                      borderRadius="small"
                      disableDefaultAnimation
                    />
                  </TeamMemberCard>
                ))}
              </div>

              <div className="grid grid-flow-row justify-items-start justify-center">
                <List>
                  <ListItem
                    onClick={handleOpenUrl(`mailto:${general.hrEmailAddress}`)}
                    onKeyDown={handleOpenUrl(
                      `mailto:${general.hrEmailAddress}`,
                    )}
                    avatar={<MailIcon />}
                    className="place-content-start"
                  >
                    {general.hrEmailAddress}
                  </ListItem>

                  <ListItem
                    onClick={handleOpenUrl(`tel:${general.hrPhoneNumber}`)}
                    onKeyDown={handleOpenUrl(`tel:${general.hrPhoneNumber}`)}
                    avatar={<PhoneIcon />}
                    className="place-content-start"
                  >
                    {general.hrPhoneNumber}
                  </ListItem>
                </List>
              </div>
            </div>

            <form
              className="grid grid-flow-row gap-2 col-span-12 lg:col-span-2"
              onSubmit={onSubmit(handleSubmit)}
              autoComplete="off"
            >
              <div className="grid sm:grid-cols-2 sm:gap-8">
                <InputField
                  {...register('name', {
                    required: {
                      value: true,
                      message: t('common:validation.required'),
                    },
                    maxLength: {
                      value: 255,
                      message: t('common:validation.max-length', {count: 255}),
                    },
                  })}
                  id="name"
                  label={t('form.name')}
                  placeholder={t('form.name-placeholder')}
                  required
                  error={!!errors.name}
                  hint={errors.name?.message}
                  autoComplete="off"
                />

                <InputField
                  {...register('email', {
                    required: {
                      value: true,
                      message: t('common:validation.required'),
                    },
                    maxLength: {
                      value: 255,
                      message: t('common:validation.max-length', {count: 255}),
                    },
                  })}
                  id="email-address"
                  label={t('form.email-address')}
                  placeholder={t('form.email-address-placeholder')}
                  type="email"
                  required
                  error={!!errors.email}
                  hint={errors.email?.message}
                  autoComplete="off"
                />
              </div>

              <div className="grid sm:grid-flow-col sm:gap-8">
                <InputField
                  {...register('phone', {
                    maxLength: {
                      value: 255,
                      message: t('common:validation.max-length', {count: 255}),
                    },
                  })}
                  id="phone-number"
                  label={t('form.phone-number')}
                  placeholder={t('form.phone-number-placeholder')}
                  type="tel"
                  error={!!errors.phone}
                  hint={errors.phone?.message}
                  autoComplete="off"
                />

                <InputField
                  {...register('position', {
                    maxLength: {
                      value: 255,
                      message: t('common:validation.max-length', {count: 255}),
                    },
                  })}
                  id="position"
                  label={t('form.position')}
                  placeholder={t('form.position-placeholder')}
                  error={!!errors.position}
                  hint={errors.position?.message}
                />
              </div>

              <InputField
                {...register('interest', {
                  maxLength: {
                    value: 2500,
                    message: t('common:validation.max-length', {count: 2500}),
                  },
                })}
                id="interest"
                label={t('form.interest')}
                placeholder={t('form.interest-placeholder')}
                multiline
                error={!!errors.interest}
                hint={errors.interest?.message}
              />

              <FileInput
                {...register('attachments')}
                id="attachments"
                label={t('form.attachments')}
                placeholder={t('form.attachments-placeholder')}
                ctaPlaceholder={t('form.attachments-cta-placeholder')}
                multiple
                max={3}
                accept=".doc,.docx,.pdf"
                error={!!errors.attachments}
                hint={errors.attachments?.message ?? t('form.attachments-hint')}
                onRemove={handleFileRemove}
                files={files}
                onFileChange={setFiles}
              />

              <Button
                type="submit"
                variant="primary"
                className="justify-self-end relative"
                disabled={isSubmitting}
              >
                <ActivityIndicator
                  className={clsx(
                    'absolute top-1/2 -translate-y-1/2 left-0 right-0 mx-auto transition-opacity opacity-0 z-0 scale-75',
                    isSubmitting && 'opacity-100',
                  )}
                />

                <span
                  className={clsx(
                    isSubmitting && 'opacity-0',
                    'transition-opacity z-20',
                  )}
                >
                  {t('form.submit')}
                </span>
              </Button>
            </form>
          </ContactForm>
        </Container>

        {nextSteps && nextSteps.length > 0 && (
          <NextStepsSection
            title={t('next-steps.title')}
            nextSteps={nextSteps}
            className={clsx(
              nextStepsInView
                ? 'motion-safe:animate-fade-in-slide-up-delay-md'
                : 'opacity-0',
            )}
            ref={nextStepsRef}
          />
        )}
      </PageLayout>
    </FooterProvider>
  )
}

 async function _getStaticProps() {
  const {
    coverTitle,
    coverDescription: rawCoverDescription,
    metaDescription,
    previewImage,
    responsiblePeople,
    nextSteps,
  } = fetchContactUsData('careers')

  const coverDescription = await serialize(rawCoverDescription)

  return {
    props: {
      coverTitle,
      coverDescription,
      metaDescription,
      previewImage,
      responsiblePeople: responsiblePeople.map(mapRawTeamMemberToTeamMember),
      nextSteps,
      mailApiUrl: process.env.MAIL_API_URL || '',
      social: fetchSocialLinks(),
      general: fetchGeneralSettings(),
    },
  }
}


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/contact-us/apply',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  